import { createContext, useState, useContext } from 'react';
import Snackbar from '@mui/material/Snackbar';
import Alert, { AlertProps } from '@mui/material/Alert';
import { useTheme } from '@mui/material/styles';

interface CustomAlertProps extends AlertProps {
  background?: 'primary' | 'secondary'; // cor customizada
  icon?: React.ReactNode;               // ícone customizável
}

interface SnackbarContextProps {
  showSnackbar: (message: string | React.ReactNode, options?: CustomAlertProps) => void;
}

export const SnackbarContext = createContext<SnackbarContextProps>({
  showSnackbar: () => {},
});

export const SnackbarProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const theme = useTheme();
  const [isOpen, setIsOpen] = useState(false);
  const [message, setMessage] = useState<string | React.ReactNode>('');
  const [options, setOptions] = useState<CustomAlertProps>({});

  const handleClose = () => {
    setIsOpen(false);
  };

  const showSnackbar = (msg: string | React.ReactNode, opts: CustomAlertProps = {}) => {
    setMessage(msg);
    setOptions(opts);
    setIsOpen(true);
  };

  const getCustomStyles = () => {
    if (options.background === 'primary') {
      return {
        backgroundColor: theme.palette.primary.main,
        color: theme.palette.getContrastText(theme.palette.primary.main),
        '& .MuiAlert-icon': {
          color: theme.palette.getContrastText(theme.palette.primary.main),
        },
      };
    }
  
    if (options.background === 'secondary') {
      return {
        backgroundColor: theme.palette.secondary.main,
        color: theme.palette.getContrastText(theme.palette.secondary.main),
        '& .MuiAlert-icon': {
          color: theme.palette.getContrastText(theme.palette.secondary.main),
        },
      };
    }
  
    return {};
  };
  

  return (
    <SnackbarContext.Provider value={{ showSnackbar }}>
      {children}
      <Snackbar open={isOpen} autoHideDuration={4000} onClose={handleClose} sx={{ zIndex: 20000 }}>
        <Alert
          onClose={handleClose}
          icon={options.icon}
          severity={options.severity}
          sx={{
            ...getCustomStyles(),
            ...options.sx,
          }}
        >
          {message}
        </Alert>
      </Snackbar>
    </SnackbarContext.Provider>
  );
};

export const useSnackbar = () => {
  const { showSnackbar } = useContext(SnackbarContext);
  return showSnackbar;
};
